import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert } from "components/bootstrap";
import { Icon, Spinner } from "components/common";
import generateId from "logic/generateId";
import useSearchExecutors from "views/components/contexts/useSearchExecutors";
import Search from "views/logic/search/Search";
import createSearch from "views/logic/slices/createSearch";
import SearchExecutionState from "views/logic/search/SearchExecutionState";
import Query, { newFiltersForQuery } from "views/logic/queries/Query";
const GEO_COORDINATES_FIELD = "source_geo_coordinates";
const constructFieldCheckSearch = (userField, query, streams, streamCategories, filters, lookbackMs, searchTypeId, queryId) => {
  var _a;
  const existsQuery = `_exists_:${GEO_COORDINATES_FIELD} AND _exists_:${userField}`;
  const fullQuery = (query == null ? void 0 : query.trim()) ? `(${query.trim()}) AND ${existsQuery}` : existsQuery;
  const rangeInSeconds = Math.max(Math.floor(lookbackMs / 1e3), 1);
  const builtQuery = Query.builder().id(queryId).query({ type: "elasticsearch", query_string: fullQuery }).timerange({ type: "relative", range: rangeInSeconds }).filter((_a = newFiltersForQuery(streams, streamCategories)) != null ? _a : null).filters(filters).searchTypes([
    {
      id: searchTypeId,
      type: "messages",
      limit: 1,
      offset: 0,
      filter: void 0,
      filters: void 0,
      name: void 0,
      query: void 0,
      timerange: void 0,
      streams: [],
      stream_categories: [],
      sort: [],
      decorators: []
    }
  ]).build();
  return Search.create().toBuilder().queries([builtQuery]).build();
};
const ImpossibleTravelFieldCheck = ({
  userField,
  query = null,
  streams,
  streamCategories = [],
  filters = [],
  lookbackMs
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const { t } = useTranslation("enterprise_security_events");
  const queryId = useRef(generateId()).current;
  const searchTypeId = useRef(generateId()).current;
  const [results, setResults] = useState();
  const [hasSearched, setHasSearched] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const { startJob, executeJobResult } = useSearchExecutors();
  useEffect(() => {
    if (!(userField == null ? void 0 : userField.trim())) return void 0;
    let cancelled = false;
    const timer = setTimeout(() => {
      setHasSearched(false);
      setSearchFailed(false);
      const search = constructFieldCheckSearch(
        userField,
        query,
        streams,
        streamCategories,
        filters,
        lookbackMs,
        searchTypeId,
        queryId
      );
      createSearch(search).then((createdSearch) => startJob(createdSearch, [searchTypeId], SearchExecutionState.empty())).then((jobIds) => executeJobResult({ jobIds })).then((result) => {
        if (!cancelled) {
          setResults(result);
          setHasSearched(true);
        }
      }).catch(() => {
        if (!cancelled) {
          setResults(void 0);
          setSearchFailed(true);
          setHasSearched(true);
        }
      });
    }, 500);
    return () => {
      clearTimeout(timer);
      cancelled = true;
    };
  }, [
    userField,
    query,
    streams,
    streamCategories,
    filters,
    lookbackMs,
    startJob,
    executeJobResult,
    queryId,
    searchTypeId
  ]);
  if (!(userField == null ? void 0 : userField.trim())) {
    return null;
  }
  const searchResult = (_c = (_b = (_a = results == null ? void 0 : results.result) == null ? void 0 : _a.forId(queryId)) == null ? void 0 : _b.searchTypes) == null ? void 0 : _c[searchTypeId];
  const errors = (_e = (_d = results == null ? void 0 : results.result) == null ? void 0 : _d.errors) != null ? _e : [];
  const hasError = searchFailed || errors.length > 0;
  const isFetching = !hasSearched;
  const totalResults = (_h = (_g = searchResult == null ? void 0 : searchResult.total_results) != null ? _g : (_f = searchResult == null ? void 0 : searchResult.messages) == null ? void 0 : _f.length) != null ? _h : 0;
  const hasMessages = totalResults > 0;
  if (isFetching) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(Spinner, { text: t("impossible_travel_field_check.checking") }));
  }
  if (hasError) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, /* @__PURE__ */ React.createElement(Icon, { name: "warning" }), " ", t("impossible_travel_field_check.check_failed"));
  }
  if (hasMessages) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "success" }, /* @__PURE__ */ React.createElement(Icon, { name: "check" }), " ", /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "impossible_travel_field_check.messages_found",
        values: { userField, geoField: GEO_COORDINATES_FIELD },
        components: { code: /* @__PURE__ */ React.createElement("code", null) }
      }
    ));
  }
  return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, /* @__PURE__ */ React.createElement(Icon, { name: "warning" }), " ", /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "impossible_travel_field_check.no_messages_found",
      values: { userField, geoField: GEO_COORDINATES_FIELD },
      components: { code: /* @__PURE__ */ React.createElement("code", null) }
    }
  ));
};
export default ImpossibleTravelFieldCheck;
